import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import api from '../api/axios';
import QuestionDisplay from './QuestionDisplay';
import { loadSession, clearExam } from '../store/examSlice';

const TutorMode = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const group = location.state?.group || '';
  const customQuestions = location.state?.questions; // for review incorrect
  const savedSession = useSelector((state) => state.exam);

  const [questions, setQuestions] = useState(customQuestions || []);
  const [currentIndex, setCurrentIndex] = useState(
    savedSession.mode === 'tutor' && savedSession.questions.length ? savedSession.currentIndex : 0
  );
  const [selected, setSelected] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(!customQuestions && !savedSession.questions.length);
  const [timeSpent, setTimeSpent] = useState(0);
  const [sessionResults, setSessionResults] = useState(
    savedSession.mode === 'tutor' && savedSession.results ? savedSession.results : []
  );
  const [submitted, setSubmitted] = useState(false);
  const timerRef = useRef(null);

  // Helper to bold answer letters
  const formatExplanation = (text) => {
    if (!text) return '';
    return text.replace(/([A-F]\.|\s[A-F]\s–)/g, '<strong>$&</strong>');
  };

  // Helper to parse explanation into sections
  const parseExplanation = (text) => {
    if (!text) return { correctPoints: [], incorrectPoints: [], fullText: '' };

    let correctPart = '';
    let incorrectPart = '';

    const correctHeaders = [
      'Rationale for correct answer:',
      'Rationale for correct answers:',
      '# Rationale for correct answer:',
      '# Rationale for correct answers:'
    ];
    const incorrectHeaders = [
      'Rationale for incorrect answer:',
      'Rationale for incorrect answers:',
      '# Rationale for incorrect answer:',
      '# Rationale for incorrect answers:'
    ];

    for (let h of correctHeaders) {
      if (text.includes(h)) {
        correctPart = text.split(h)[1] || '';
        break;
      }
    }
    for (let h of incorrectHeaders) {
      if (text.includes(h)) {
        incorrectPart = text.split(h)[1] || '';
        break;
      }
    }

    const extractPoints = (section) => {
      if (!section) return [];
      const pointRegex = /\d+\.\s+[^\n]+/g;
      const matches = section.match(pointRegex);
      return matches ? matches.map(m => m.trim()) : [];
    };

    return {
      correctPoints: extractPoints(correctPart),
      incorrectPoints: extractPoints(incorrectPart),
      fullText: text
    };
  };

  // Restore selected answer from saved session
  useEffect(() => {
    if (savedSession.mode === 'tutor' && savedSession.answers) {
      const qid = savedSession.questions[currentIndex]?.id;
      if (qid && savedSession.answers[qid]) {
        setSelected(savedSession.answers[qid]);
        setSubmitted(true);
      }
    }
  }, [currentIndex, savedSession]);

  // Fetch questions if not provided and no saved session
  useEffect(() => {
    const fetchQuestions = async () => {
      if (customQuestions) {
        setQuestions(customQuestions);
        setLoading(false);
        return;
      }
      if (savedSession.mode === 'tutor' && savedSession.questions.length) {
        setQuestions(savedSession.questions);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const config = location.state || {};
        const numQuestions = config.numQuestions || 10;
        const filter = config.filter || null;
        const group = config.group || '';
        let url = `/questions.php?limit=${numQuestions}`;
        if (group) url += `&group=${group}`;
        if (filter) url += `&filter=${filter}`;
        const res = await api.get(url);
        setQuestions(res.data);
      } catch (err) {
        console.error(err);
        alert('Failed to load questions');
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [customQuestions, savedSession, location.state]);

  // Timer
  useEffect(() => {
    if (feedback) {
      if (timerRef.current) clearInterval(timerRef.current);
    } else {
      timerRef.current = setInterval(() => {
        setTimeSpent(prev => prev + 0.01);
      }, 10);
      return () => clearInterval(timerRef.current);
    }
  }, [feedback, currentIndex]);

  useEffect(() => {
    setTimeSpent(0);
  }, [currentIndex]);

  const handleSelect = (opt) => {
    if (!submitted) setSelected(opt);
  };

  const calculateScore = (question, selectedOpts) => {
    if (!selectedOpts) return 0;
    const correctSet = question.correct_answer.split(',');
    const selectedSet = selectedOpts.split(',');
    if (question.mode === 'MC') {
      return selectedOpts === question.correct_answer ? 1 : 0;
    } else {
      const correctSelected = selectedSet.filter(opt => correctSet.includes(opt)).length;
      const incorrectSelected = selectedSet.filter(opt => !correctSet.includes(opt)).length;
      return Math.max(0, correctSelected - incorrectSelected);
    }
  };

  const handleSubmit = async () => {
    if (!selected) return alert('Please select an answer');
    try {
      const res = await api.post('/answers.php', {
        questionId: questions[currentIndex].id,
        selectedOptions: selected
      });
      setFeedback(res.data);
      const statsRes = await api.get(`/stats.php?questionId=${questions[currentIndex].id}`);
      setStats(statsRes.data);

      const score = calculateScore(questions[currentIndex], selected);
      setSubmitted(true);

      setSessionResults(prev => {
        const existingIndex = prev.findIndex(item => item.question.id === questions[currentIndex].id);
        const newItem = {
          question: questions[currentIndex],
          selected,
          correct: res.data.isCorrect,
          score,
          timeSpent: timeSpent,
          explanation: res.data.explanation
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = newItem;
          return updated;
        } else {
          return [...prev, newItem];
        }
      });
    } catch (err) {
      console.error(err);
      alert('Error submitting answer');
    }
  };

  const handleNext = () => {
    setSelected('');
    setFeedback(null);
    setStats(null);
    setSubmitted(false);
    if (currentIndex + 1 < questions.length) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePause = () => {
    const sessionState = {
      mode: 'tutor',
      sessionId: null,
      currentIndex,
      questions,
      answers: sessionResults.reduce((acc, item) => {
        acc[item.question.id] = item.selected;
        return acc;
      }, {}),
      results: sessionResults,
      completed: false,
      score: null,
    };
    dispatch(loadSession(sessionState));
    navigate('/dashboard');
  };

  const handleEnd = () => {
    navigate('/tutor/report', { state: { results: sessionResults, totalQuestions: questions.length } });
  };

  if (loading) return <Container className="mt-4"><Spinner animation="border" /></Container>;
  if (!questions.length) return <Container className="mt-4"><Alert variant="warning">No questions available.</Alert></Container>;

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const score = feedback ? calculateScore(currentQuestion, selected) : 0;
  const maxScore = currentQuestion.mode === 'MC' ? 1 : currentQuestion.correct_answer.split(',').length;
  const scoringRule = currentQuestion.mode === 'MC' ? '0/1 Scoring' : 'Right minus wrong (min 0)';

  const { correctPoints, incorrectPoints, fullText } = feedback
    ? parseExplanation(feedback.explanation)
    : { correctPoints: [], incorrectPoints: [], fullText: '' };

  const hasPoints = correctPoints.length > 0 || incorrectPoints.length > 0;

  return (
    <Container className="mt-4" style={{ maxWidth: '1200px', marginLeft: '20px' }}>
      <h2 className="mb-3">Tutor Mode</h2>
      <p className="text-muted mb-4">Question {currentIndex + 1} of {questions.length}</p>

      {!feedback ? (
        /* Before submission – single column */
        <div>
          <div className="mb-3">
            <p><strong>{currentQuestion.question_text}</strong></p>
            <QuestionDisplay
              question={currentQuestion}
              selectedOptions={selected}
              onSelect={handleSelect}
              showFeedback={false}
              showCorrectness={false}
            />
          </div>
          <Button variant="primary" onClick={handleSubmit} className="mb-3">
            Submit
          </Button>
        </div>
      ) : (
        /* After submission – two columns */
        <Row>
          <Col md={7}>
            <div className="mb-3">
              <p><strong>{currentQuestion.question_text}</strong></p>
              <QuestionDisplay
                question={currentQuestion}
                selectedOptions={selected}
                onSelect={handleSelect}
                showFeedback={false}
                showCorrectness={true}
              />
            </div>
            {/* Score row */}
            <div className="mb-3 p-3 bg-light rounded">
              <div className="d-flex justify-content-between border-bottom pb-2">
                <span><strong>Scored/Max</strong></span>
                <span><strong>Scoring Rule</strong></span>
                <span><strong>Time Spent</strong></span>
                <span><strong>Answered Correctly</strong></span>
              </div>
              <div className="d-flex justify-content-between pt-2">
                <span>{score}/{maxScore}</span>
                <span>{scoringRule}</span>
                <span>{timeSpent.toFixed(2)}s</span>
                <span>{stats ? `${stats.percent_correct}%` : 'N/A'}</span>
              </div>
            </div>
          </Col>

          {/* Right panel – explanation only (no static sections) */}
          <Col md={5}>
            <div
              className="p-4 bg-light rounded shadow-sm"
              style={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
              {hasPoints ? (
                <>
                  {correctPoints.length > 0 && (
                    <>
                      <h5 className="mb-3">Rationale for correct answers:</h5>
                      {correctPoints.map((point, idx) => (
                        <div key={idx} className="mb-3" style={{ lineHeight: 1.6 }}>
                          {point}
                        </div>
                      ))}
                    </>
                  )}
                  {incorrectPoints.length > 0 && (
                    <>
                      <hr className="my-4" />
                      <h5 className="mb-3">Rationale for incorrect answers:</h5>
                      {incorrectPoints.map((point, idx) => (
                        <div key={idx} className="mb-3" style={{ lineHeight: 1.6 }}>
                          {point}
                        </div>
                      ))}
                    </>
                  )}
                </>
              ) : (
                // Fallback: formatted full explanation
                <>
                  <h5 className="mb-3">Explanation:</h5>
                  <div
                    className="mb-3"
                    style={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
                    dangerouslySetInnerHTML={{ __html: formatExplanation(fullText) }}
                  />
                </>
              )}
              {/* Next/End button */}
              <div className="d-grid gap-2 mt-4">
                {isLastQuestion ? (
                  <Button variant="primary" size="lg" onClick={handleEnd}>
                    End Session
                  </Button>
                ) : (
                  <Button variant="primary" size="lg" onClick={handleNext}>
                    Next Question →
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Back to dashboard button */}
      <div className="mt-3">
        <Button variant="outline-primary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    </Container>
  );
};

export default TutorMode;